import { Box, Link, Typography } from "@mui/material";
import type { ReactNode } from "react";

import type { StatusTone } from "../utils/overview";

export const OVERVIEW_COLORS = {
  ink: "#1F2733",
  ink2: "#3C4552",
  muted: "#5B6573",
  muted2: "#8A94A3",
  line: "#E3E7ED",
  line2: "#EEF0F3",
  headBg: "#F7F8FA",
  blue: "#1565C0",
  blueSoft: "#EEF4FC",
  red: "#C62828",
  orange: "#E8710A",
};

/** Paleta única dos selos de status/gravidade na tela inteira. */
export const TONE_STYLE: Record<StatusTone, { bg: string; fg: string }> = {
  green: { bg: "#E6F4EA", fg: "#1B5E20" },
  orange: { bg: "#FDF0DC", fg: "#8A4B00" },
  blue: { bg: "#E3ECF8", fg: "#0D47A1" },
  red: { bg: "#FDE7E9", fg: "#A50E1C" },
  gray: { bg: "#EEF0F3", fg: "#4A5360" },
};

export function StatusPill({
  tone,
  children,
  small = false,
}: {
  tone: StatusTone;
  children: ReactNode;
  small?: boolean;
}) {
  const { bg, fg } = TONE_STYLE[tone];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        fontSize: small ? 11 : 12,
        fontWeight: 500,
        lineHeight: 1.4,
        px: small ? 1 : 1.25,
        py: small ? 0.125 : 0.375,
        borderRadius: "12px",
        whiteSpace: "nowrap",
        bgcolor: bg,
        color: fg,
      }}
    >
      {children}
    </Box>
  );
}

export function OverviewCard({
  title,
  action,
  extra,
  children,
  testId,
}: {
  title: string;
  action?: ReactNode;
  extra?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <Box
      component="section"
      data-testid={testId}
      aria-label={title}
      sx={{
        bgcolor: "#fff",
        border: `1px solid ${OVERVIEW_COLORS.line}`,
        borderRadius: "10px",
        px: 3,
        py: 2.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minWidth: 0,
      }}
    >
      <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
        <Typography
          component="h2"
          sx={{ m: 0, fontSize: 17, fontWeight: 500, color: OVERVIEW_COLORS.ink }}
        >
          {title}
        </Typography>
        {extra}
        <Box flex={1} />
        {action}
      </Box>
      {children}
    </Box>
  );
}

export function CardLink({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Link
      component="button"
      type="button"
      underline="hover"
      onClick={onClick}
      sx={{ fontSize: 13, color: OVERVIEW_COLORS.blue }}
    >
      {children}
    </Link>
  );
}

export function Field({
  label,
  value,
}: {
  label: string;
  value?: ReactNode;
}) {
  const empty = value === undefined || value === null || value === "";
  return (
    <Box minWidth={0}>
      <Typography
        component="small"
        sx={{
          display: "block",
          fontSize: 12,
          color: OVERVIEW_COLORS.muted,
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          color: empty ? OVERVIEW_COLORS.muted2 : OVERVIEW_COLORS.ink,
          overflowWrap: "anywhere",
        }}
      >
        {empty ? "Não definido" : value}
      </Typography>
    </Box>
  );
}

export function FieldGrid({
  columns = 2,
  children,
}: {
  columns?: 2 | 3;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          sm: `repeat(${columns}, minmax(0, 1fr))`,
        },
      }}
    >
      {children}
    </Box>
  );
}
